import { _isNotEmpty } from '@/littledash';
import type { SendCoverageGridRow } from '@/model/Agent.model';
import type { FC } from 'react';
import { sendDomainLabel } from '@/components/Agents/SendValidator/utils/SendDomains';
import styles from './SendResults.module.scss';

interface SendCoverageGridProps {
  rows: ReadonlyArray<SendCoverageGridRow>;
  testId?: string;
}

/** The server's key for rules that apply to the bundle as a whole rather than to one dataset. */
const BUNDLE_ROW = 'bundle';

/**
 * How the published rules landed for each dataset, as the server counted them, plus the rules that
 * apply to the bundle as a whole. Every cell is the server's number, read as given.
 */
const SendCoverageGrid: FC<SendCoverageGridProps> = ({ rows, testId = 'invivo-send-validator__coverage-grid' }) => {
  if (!_isNotEmpty(rows)) {
    return null;
  }
  return (
    <div className={`${styles.gridScroll} mt3`} data-test-component="SendCoverageGrid" data-testid={testId}>
      <table className={styles.grid}>
        <caption className={styles.gridCaption}>Rules by dataset</caption>
        <thead>
          <tr>
            <th scope="col">Dataset</th>
            <th scope="col">Passed</th>
            <th scope="col">Failed</th>
            <th scope="col">Not applicable</th>
            <th scope="col">Not evaluated</th>
            <th scope="col">Rules</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, tally }) => (
            <tr key={key} data-test-element="coverage-row" data-test-key={key}>
              <th scope="row">{key === BUNDLE_ROW ? 'The bundle as a whole' : sendDomainLabel(key)}</th>
              <td data-test-element="coverage-passed">
                {tally.passed}
                {/* As the tally tiles do: a pass short of an exact, finished check is never read as one. */}
                {tally.passed > tally.conforming && (
                  <span className={styles.cellNote}>{tally.passed - tally.conforming} in part</span>
                )}
              </td>
              <td>{tally.failed}</td>
              <td>{tally.not_applicable}</td>
              <td>{tally.not_evaluated}</td>
              <td>{tally.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SendCoverageGrid;
